import { enabledLanguages } from "@app/i18n"
import { PermissionType } from "@shared/api/authentication/types"
import {
	getLanguageFromLocale,
	resolveToSupportedLanguage as resolveLanguageCodeToSupportedLocale,
} from "@shared/i18n/utils"
import { useAuth } from "@shared/lib/auth"
import { useUserSettingsStore, useCountriesStore } from "@shared/stores"
import { useCallback, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"

import type { LanguageOption } from "./types"

interface UseLanguageSettingsResult {
	availableLanguages: LanguageOption[]
	selectedLocale: string | undefined
	handleLanguageChanged: (languageCode: string) => Promise<void>
}

export const useLanguageSettings = (): UseLanguageSettingsResult => {
	const { t: tLanguages } = useTranslation("shared", {
		keyPrefix: "languages",
	})

	const [userLocale, updateUserLocale] = useUserSettingsStore(store => [
		store.currentLanguage,
		store.updateLanguage,
	])

	const [countries, fetchCountries] = useCountriesStore(store => [
		store.countries,
		store.fetchCountries,
	])

	const { userData, userPermissions, updateUserProfile } = useAuth()

	const isCustomer = useMemo(
		() =>
			userPermissions
				?.map(x => x.permissionType)
				.includes(PermissionType.Customer) ?? false,
		[userPermissions],
	)

	useEffect(() => {
		fetchCountries()
	}, [fetchCountries])

	const languagesByUserCountry = useMemo(() => {
		if (!countries || !userData?.country) {
			return enabledLanguages
		}

		return (
			countries
				.find(c => c.countryCode === userData?.country)
				?.locales.map(l => l.code) ?? enabledLanguages
		)
	}, [countries, userData?.country])

	const availableLanguages = useMemo(() => {
		if (!isCustomer) {
			const englishLocale = enabledLanguages.find(sl => sl === "en-US")!
			return [{ key: englishLocale, title: tLanguages(englishLocale) }]
		}

		return enabledLanguages
			.filter(sl =>
				languagesByUserCountry.some(
					luc =>
						getLanguageFromLocale(luc) ===
						getLanguageFromLocale(sl),
				),
			)
			.map(languageCode => ({
				key: languageCode,
				title: tLanguages(languageCode),
			}))
	}, [isCustomer, languagesByUserCountry, tLanguages])

	const selectedLocale = useMemo(
		() =>
			resolveLanguageCodeToSupportedLocale(
				userLocale,
				availableLanguages.map(l => l.key),
			),
		[userLocale, availableLanguages],
	)

	const handleLanguageChanged = useCallback(
		async (languageCode: string) => {
			const supportedUserLanguage = resolveLanguageCodeToSupportedLocale(
				languageCode,
				languagesByUserCountry,
			)

			if (!supportedUserLanguage) return
			await updateUserProfile({ defaultLocale: supportedUserLanguage })
			updateUserLocale(supportedUserLanguage)
		},
		[languagesByUserCountry, updateUserProfile, updateUserLocale],
	)

	return {
		availableLanguages,
		selectedLocale,
		handleLanguageChanged,
	}
}
